import type {
    WhatsappAccountResponse,
    WhatsappConversation,
    WhatsappConversationListResponse,
    WhatsappConversationStatus,
    WhatsappMessageListResponse,
} from './types';

import { baseRequest } from '@/lib/base';
import { extractApiError } from '@/lib/shared-utils';

export interface ConnectWhatsappAccountPayload {
    phone_number_id: string;
    access_token: string;
    waba_id: string;
    app_secret?: string;
    pin?: string;
}

export interface FetchConversationsParams {
    status?: WhatsappConversationStatus;
    is_read?: boolean;
    q?: string;
    page?: number;
    page_size?: number;
}

export interface FetchMessagesParams {
    page?: number;
    page_size?: number;
}

export async function fetchWhatsappAccount(): Promise<WhatsappAccountResponse> {
    const response = await baseRequest({ url: '/whatsapp/account/' });
    if (!response.ok) throw new Error(extractApiError(response.data, 'Failed to load the WhatsApp account.'));
    return response.data;
}

export const connectWhatsappAccount = (payload: ConnectWhatsappAccountPayload) => baseRequest({
    url: '/whatsapp/account/connect/',
    method: 'POST',
    body: payload,
    contentType: 'application/json',
});

export const disconnectWhatsappAccount = () => baseRequest({
    url: '/whatsapp/account/disconnect/',
    method: 'POST',
});

export async function fetchConversations(
    params: FetchConversationsParams = {},
): Promise<WhatsappConversationListResponse> {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.is_read !== undefined) search.set('is_read', String(params.is_read));
    if (params.q) search.set('q', params.q);
    search.set('page', String(params.page ?? 1));
    search.set('page_size', String(params.page_size ?? 20));

    const response = await baseRequest({ url: `/whatsapp/conversations/?${search.toString()}` });
    if (!response.ok) throw new Error(extractApiError(response.data, 'Failed to load conversations.'));
    return response.data;
}

// Number of conversations currently unread — used for the sidebar badge.
export async function fetchWhatsappUnreadConversationCount(): Promise<number> {
    const response = await baseRequest({ url: '/whatsapp/conversations/?is_read=false&page_size=1' });
    if (!response.ok) return 0;
    return (response.data as WhatsappConversationListResponse)?.count ?? 0;
}

export async function fetchConversation(uuid: string): Promise<WhatsappConversation> {
    const response = await baseRequest({ url: `/whatsapp/conversations/${uuid}/` });
    if (!response.ok) throw new Error(extractApiError(response.data, 'Failed to load the conversation.'));
    return response.data;
}

export const markConversationRead = (uuid: string) => baseRequest({
    url: `/whatsapp/conversations/${uuid}/read/`,
    method: 'POST',
});

export async function fetchMessages(
    uuid: string,
    params: FetchMessagesParams = {},
): Promise<WhatsappMessageListResponse> {
    const search = new URLSearchParams();
    search.set('page', String(params.page ?? 1));
    search.set('page_size', String(params.page_size ?? 30));

    const response = await baseRequest({ url: `/whatsapp/conversations/${uuid}/messages/?${search.toString()}` });
    if (!response.ok) throw new Error(extractApiError(response.data, 'Failed to load messages.'));
    return response.data;
}

export const sendWhatsappReply = (uuid: string, body: string) => baseRequest({
    url: `/whatsapp/conversations/${uuid}/reply/`,
    method: 'POST',
    body: { body },
    contentType: 'application/json',
});
